import { NavLink, useLocation } from 'react-router-dom';
import {
  Smartphone, CreditCard, ChefHat, Package, ClipboardList, Sandwich,
  UtensilsCrossed, Tag, SlidersHorizontal, Ticket, LayoutDashboard, UsersRound, Target,
  Settings, Receipt, Users, ShieldCheck,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

type LinkDef = {
  to: string;
  label: string;
  Icon: React.ElementType;
  roles: string[];
  permiso?: string;
  estacion?: 'cocina' | 'barra';
};

type Group = {
  label: string;
  links: LinkDef[];
};

// Arquitectura del rediseño: 7 grupos por lugar de trabajo (Servicio, Caja,
// Cocina, Almacén, Menú, Reportes, Ajustes). Las URLs no cambian; solo los
// nombres. Páginas que el diseño no trae se quedan en el grupo que les toca.
export const NAV_GROUPS: Group[] = [
  {
    label: 'Servicio',
    links: [
      { to: '/dashboard',     Icon: LayoutDashboard, label: 'Inicio', roles: ['admin','cajero'] },
      { to: '/comandera',     Icon: Smartphone, label: 'Salón',   roles: ['admin','mesero'] },
    ],
  },
  {
    label: 'Caja',
    links: [
      { to: '/caja',              Icon: CreditCard, label: 'Cuentas', roles: ['admin','cajero'] },
    ],
  },
  {
    label: 'Cocina',
    links: [
      { to: '/comandas', Icon: ChefHat, label: 'Comandas', roles: ['admin','cocina'] },
    ],
  },
  {
    label: 'Almacén',
    links: [
      { to: '/inventario',       Icon: Package, label: 'Inventario',       roles: ['admin','cocina','almacenista'] },
      { to: '/requisiciones',    Icon: ClipboardList, label: 'Requisiciones',    roles: ['admin','cocina','almacenista'] },
      { to: '/comida-empleados', Icon: Sandwich, label: 'Comida empleados', roles: ['admin','cocina','almacenista'] },
    ],
  },
  {
    label: 'Menú',
    links: [
      { to: '/productos',     Icon: UtensilsCrossed, label: 'Productos',     roles: ['admin'] },
      { to: '/categorias',    Icon: Tag, label: 'Categorías',    roles: ['admin'] },
      { to: '/modificadores', Icon: SlidersHorizontal, label: 'Modificadores', roles: ['admin'] },
      { to: '/promociones',   Icon: Ticket, label: 'Promociones',   roles: ['admin'] },
    ],
  },
  {
    label: 'Reportes',
    links: [
      { to: '/reportes/clientes', Icon: UsersRound, label: 'Clientes y operación', roles: ['admin','cajero'], permiso: 'puede_ver_reportes' },
      { to: '/objetivos',      Icon: Target, label: 'Objetivos',    roles: ['admin'] },
    ],
  },
  {
    label: 'Ajustes',
    links: [
      { to: '/config-ticket',  Icon: Settings, label: 'Configuración', roles: ['admin'] },
      { to: '/config-tickets', Icon: Receipt, label: 'Tickets',       roles: ['admin'] },
      { to: '/usuarios',       Icon: Users, label: 'Equipo',    roles: ['admin'] },
      { to: '/auditoria',      Icon: ShieldCheck, label: 'Auditoría', roles: ['admin'], permiso: 'puede_ver_auditoria' },
    ],
  },
];

export function isActive(pathname: string, to: string) {
  return pathname === to || pathname.startsWith(to + '/');
}

// Grupos filtrados por rol, permiso granular y estación de KDS.
export function useNavVisible() {
  const { usuario } = useAuth();
  const rol = usuario?.rol || '';
  return NAV_GROUPS
    .map(g => ({
      ...g,
      links: g.links.filter(l => {
        const porRolOPermiso = l.roles.includes(rol) || (l.permiso && !!usuario?.[l.permiso]);
        if (!porRolOPermiso) return false;
        return !(l.estacion && rol === 'cocina' && usuario?.estacion && usuario.estacion !== 'ambas' && usuario.estacion !== l.estacion);
      }),
    }))
    .filter(g => g.links.length > 0);
}

// Estilo del menú anterior al rediseño: iconos, letra compacta, el activo en
// el color de marca. Plegado queda como riel de iconos (con el nombre al pasar).
export function navItemStyle(active: boolean, compacto = false): React.CSSProperties {
  return {
    display: 'flex', alignItems: 'center', gap: 10,
    minHeight: 36, padding: compacto ? '8px' : '7px 10px', borderRadius: 8,
    justifyContent: compacto ? 'center' : 'flex-start',
    fontSize: 14, fontWeight: active ? 700 : 400, textDecoration: 'none',
    background: active ? 'color-mix(in srgb, var(--brand-texto) 14%, transparent)' : 'transparent',
    color: active ? 'var(--brand-texto)' : 'var(--ink-2)',
  };
}

export default function Sidebar({ groups, compacto = false }: { groups: ReturnType<typeof useNavVisible>; compacto?: boolean }) {
  const { pathname } = useLocation();
  return (
    <aside style={{
      width: compacto ? 64 : 220, flex: 'none', background: 'var(--surface)', transition: 'width .18s ease',
      borderRight: '1px solid var(--line)', display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden',
    }}>
      <nav aria-label="Menú principal" style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', padding: '8px 8px 4px' }}>
        {groups.map(g => (
          <div key={g.label} style={{ padding: '6px 0 2px' }}>
            {compacto
              ? <div aria-hidden style={{ height: 1, background: 'var(--line)', margin: '4px 8px 6px' }} />
              : <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--faint)', padding: '2px 10px 5px', whiteSpace: 'nowrap' }}>{g.label}</div>}
            {g.links.map(({ to, label, Icon }) => {
              const on = isActive(pathname, to);
              return (
                <NavLink key={to} to={to} className="nav-item" aria-current={on ? 'page' : undefined} aria-label={compacto ? label : undefined} title={compacto ? label : undefined}
                  style={{ ...navItemStyle(on, compacto), marginBottom: 1 }}>
                  <Icon size={18} aria-hidden style={{ flexShrink: 0, color: on ? 'var(--brand-texto)' : 'var(--muted)' }} />
                  {!compacto && <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>}
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>
    </aside>
  );
}
